import { Skeleton } from '@/components/ui/skeleton';
import { motion } from 'framer-motion';

interface ProfileCardSkeletonProps {
  type: 'profile' | 'team' | 'simple' | 'account';
  delay?: number;
}

function SkeletonCard({ className = '', children }: { readonly className?: string; readonly children: React.ReactNode }) {
  return (
    <div className={`rounded-2xl bg-white/[0.03] border border-white/10 ${className}`}>
      {children}
    </div>
  );
}

export function ProfileCardSkeleton({ type, delay = 0 }: Readonly<ProfileCardSkeletonProps>) {
  if (type === 'profile') {
    return (
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
      >
        <SkeletonCard>
          <div className="p-6">
            <div className="flex items-center gap-4">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          </div>
        </SkeletonCard>
      </motion.div>
    );
  }

  if (type === 'team') {
    return (
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
      >
        <SkeletonCard>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-xl" />
                <Skeleton className="h-5 w-28" />
              </div>
              <Skeleton className="h-8 w-24 rounded-md" />
            </div>
            <div className="grid grid-cols-3 gap-6 py-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col items-center">
                  <Skeleton className="h-14 w-14 rounded-full" />
                  <Skeleton className="h-4 w-16 mt-2" />
                  <Skeleton className="h-3 w-24 mt-1" />
                </div>
              ))}
            </div>
          </div>
        </SkeletonCard>
      </motion.div>
    );
  }

  if (type === 'simple') {
    return (
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
      >
        <SkeletonCard>
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-48" />
              </div>
            </div>
            <Skeleton className="h-5 w-5" />
          </div>
        </SkeletonCard>
      </motion.div>
    );
  }

  if (type === 'account') {
    return (
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
      >
        <SkeletonCard>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <Skeleton className="h-5 w-28" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Skeleton className="h-12 rounded-md" />
              <Skeleton className="h-12 rounded-md" />
            </div>
            <Skeleton className="h-4 w-80 mx-auto mt-4" />
          </div>
        </SkeletonCard>
      </motion.div>
    );
  }

  return null;
}

// Transition wrapper for smooth skeleton to content transitions
interface ContentWithSkeletonProps {
  isLoading: boolean;
  skeleton: React.ReactNode;
  children: React.ReactNode;
}

export function ContentWithSkeleton({ isLoading, skeleton, children }: Readonly<ContentWithSkeletonProps>) {
  return (
    <motion.div
      key={isLoading ? 'skeleton' : 'content'}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {isLoading ? skeleton : children}
    </motion.div>
  );
}
